import { useMemo, useState } from 'react';
import { api, ApiError, formatDate, formatDateTime, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type {
  ConsularCase,
  ConsularCaseType,
  DiplomaticMission,
  EmergencyTravelDocDetails,
  NotarialDetails,
  Page,
  PassportRenewalDetails,
  WelfareAssistDetails,
} from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge } from '../../ui/components';

const CASE_TYPES: ConsularCaseType[] = ['PASSPORT_RENEWAL', 'EMERGENCY_TRAVEL_DOC', 'WELFARE_ASSIST', 'NOTARIAL'];
const CASE_TYPE_LABEL: Record<ConsularCaseType, string> = {
  PASSPORT_RENEWAL: 'Passport renewal',
  EMERGENCY_TRAVEL_DOC: 'Emergency travel document',
  WELFARE_ASSIST: 'Welfare assistance',
  NOTARIAL: 'Notarial act',
};
const NIN_PATTERN = /^\d{3}-\d{4}-\d-\d-\d{2}$/;

/**
 * Consular case processing — all four case types (spec: passport-renewal
 * referral, emergency travel documents, welfare assistance, notarial acts).
 * Mission officer console: GET/POST /consular-cases + the full per-type
 * workflow (acknowledge -> fee|start -> [verify-passport|link-passport-
 * application] -> resolve|reject|withdraw).
 */
export function ConsularCases() {
  const { persona } = useAuth();
  const isStaff = (persona?.roles.includes('CONSULAR_OFFICER') || persona?.roles.includes('MISSION_HEAD')) ?? false;
  const [status, setStatus] = useState('');
  const [type, setType] = useState('');
  const [missionId, setMissionId] = useState('');
  const [q, setQ] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const missionsQ = useApiQuery<Page<DiplomaticMission>>('/api/v1/m42/missions?limit=100&status=active');
  const missions = useMemo(() => missionsQ.data?.items ?? [], [missionsQ.data]);
  const missionByw = useMemo(() => new Map(missions.map((m) => [m.id, m])), [missions]);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (status) params.set('status', status);
    if (type) params.set('type', type);
    if (missionId) params.set('missionId', missionId);
    if (appliedQ) params.set('q', appliedQ);
    return `/api/v1/m42/consular-cases?${params.toString()}`;
  }, [status, type, missionId, appliedQ]);

  const listQ = useApiQuery<Page<ConsularCase>>(path);

  return (
    <div>
      <PageHeader
        title="Consular Cases"
        subtitle="Passport-renewal referrals, emergency travel documents, welfare assistance & notarial acts for the diaspora."
        actions={
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + File a case
          </button>
        }
      />

      {!isStaff ? <RoleNotice roles={['CONSULAR_OFFICER', 'MISSION_HEAD']} /> : null}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <form
          className="flex flex-wrap items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            setAppliedQ(q);
          }}
        >
          <input className="field-input !w-44" placeholder="NIN or applicant name…" value={q} onChange={(e) => setQ(e.target.value)} />
          <select className="field-input !w-auto" value={missionId} onChange={(e) => setMissionId(e.target.value)}>
            <option value="">All missions</option>
            {missions.map((m) => (
              <option key={m.id} value={m.id}>
                {m.code}
              </option>
            ))}
          </select>
          <select className="field-input !w-auto" value={type} onChange={(e) => setType(e.target.value)}>
            <option value="">All types</option>
            {CASE_TYPES.map((t) => (
              <option key={t} value={t}>
                {CASE_TYPE_LABEL[t]}
              </option>
            ))}
          </select>
          <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All states</option>
            <option value="SUBMITTED">Submitted</option>
            <option value="ACKNOWLEDGED">Acknowledged</option>
            <option value="FEE_PENDING">Fee pending</option>
            <option value="IN_PROGRESS">In progress</option>
            <option value="RESOLVED">Resolved</option>
            <option value="REJECTED">Rejected</option>
            <option value="WITHDRAWN">Withdrawn</option>
          </select>
          <button type="submit" className="btn-secondary">
            Search
          </button>
        </form>
      </div>

      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No consular cases match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Applicant</th>
                <th className="table-head">NIN</th>
                <th className="table-head">Mission</th>
                <th className="table-head">Type</th>
                <th className="table-head">State</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((c) => (
                <tr key={c.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(c.id)}>
                  <td className="table-cell font-medium">{c.applicantName}</td>
                  <td className="table-cell font-mono text-xs">{c.applicantNin}</td>
                  <td className="table-cell font-mono text-xs">{missionByw.get(c.missionId)?.code ?? c.missionId}</td>
                  <td className="table-cell text-xs">{CASE_TYPE_LABEL[c.type]}</td>
                  <td className="table-cell">
                    <StatusBadge status={c.state} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selectedId ? (
        <CaseDetailModal
          id={selectedId}
          missionByw={missionByw}
          isStaff={isStaff}
          onClose={() => setSelectedId(null)}
          onChanged={() => {
            setSelectedId(null);
            listQ.reload();
          }}
        />
      ) : null}

      {showCreate ? (
        <CreateCaseModal
          missions={missions}
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function TypeDetails({ c }: { c: ConsularCase }) {
  if (c.type === 'PASSPORT_RENEWAL') {
    const d = c.details as PassportRenewalDetails;
    return (
      <>
        {d.currentPassportNo ? <Detail label="Current passport no." value={d.currentPassportNo} mono /> : null}
        <Detail label="Renewal reason" value={d.renewalReason} />
      </>
    );
  }
  if (c.type === 'EMERGENCY_TRAVEL_DOC') {
    const d = c.details as EmergencyTravelDocDetails;
    return (
      <>
        <Detail label="Lost or stolen" value={d.lostOrStolen} />
        {d.policeReportRef ? <Detail label="Police report ref" value={d.policeReportRef} mono /> : null}
        <Detail label="Travel date" value={formatDate(d.travelDate)} />
        <Detail label="Destination" value={d.destinationCountry} />
        {d.lastKnownPassportNo ? <Detail label="Last known passport no." value={d.lastKnownPassportNo} mono /> : null}
        {d.registryCheck ? (
          <Detail
            label="M04 passport check"
            value={
              <>
                <StatusBadge status={d.registryCheck.valid ? 'VALID' : 'INVALID'} /> {d.registryCheck.status ?? ''} · checked {formatDateTime(d.registryCheck.checkedAt)}
              </>
            }
          />
        ) : null}
      </>
    );
  }
  if (c.type === 'WELFARE_ASSIST') {
    const d = c.details as WelfareAssistDetails;
    return (
      <>
        <Detail label="Assistance type" value={d.assistanceType} />
        <Detail label="Description" value={d.description} />
      </>
    );
  }
  const d = c.details as NotarialDetails;
  return (
    <>
      <Detail label="Notarial act" value={d.notarialAct} />
      <Detail label="Document description" value={d.documentDescription} />
    </>
  );
}

function CaseDetailModal({
  id,
  missionByw,
  isStaff,
  onClose,
  onChanged,
}: {
  id: string;
  missionByw: Map<string, DiplomaticMission>;
  isStaff: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const detailQ = useApiQuery<ConsularCase>(`/api/v1/m42/consular-cases/${id}`);
  const c = detailQ.data;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [showReject, setShowReject] = useState(false);
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [passportNumber, setPassportNumber] = useState('');
  const [showVerifyPassport, setShowVerifyPassport] = useState(false);
  const [m04Ref, setM04Ref] = useState('');
  const [showLinkM04, setShowLinkM04] = useState(false);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [showResolve, setShowResolve] = useState(false);

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  const hasFee = c ? c.type === 'EMERGENCY_TRAVEL_DOC' || c.type === 'NOTARIAL' : false;

  return (
    <Modal title={c ? `${c.applicantName} — ${CASE_TYPE_LABEL[c.type]}` : 'Consular case'} onClose={onClose} wide>
      {detailQ.error ? <ErrorNote message={detailQ.error} /> : null}
      {detailQ.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {c ? (
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
            <Detail label="State" value={<StatusBadge status={c.state} />} />
            <Detail label="Mission" value={missionByw.get(c.missionId)?.name ?? c.missionId} />
            <Detail label="Applicant NIN" value={c.applicantNin} mono />
            <Detail label="Contact email" value={c.contactEmail} />
            {c.contactPhone ? <Detail label="Contact phone" value={c.contactPhone} /> : null}
            <Detail label="Country of residence" value={c.countryOfResidence} />
            {hasFee ? <Detail label="Fee" value={c.feePaidAt ? `${scr(c.feeAmount)} — paid ${formatDateTime(c.feePaidAt)}` : c.feeAmount ? `${scr(c.feeAmount)} due` : '—'} /> : null}
            {c.payIntentRefNo ? <Detail label="Payment reference" value={c.payIntentRefNo} mono /> : null}
            {c.m04ApplicationRef ? <Detail label="M04 application ref" value={c.m04ApplicationRef} mono /> : null}
            {c.assignedTo ? <Detail label="Assigned to" value={c.assignedTo} /> : null}
          </div>

          <div className="grid grid-cols-2 gap-x-6 gap-y-3 border-t border-slate-100 pt-4 text-sm sm:grid-cols-3">
            <TypeDetails c={c} />
          </div>

          {c.state === 'FEE_PENDING' ? (
            <div className="rounded-lg bg-violet-50 px-3.5 py-2.5 text-sm text-violet-800">
              Awaiting SeyPay confirmation for reference <span className="font-mono font-semibold">{c.payIntentRefNo}</span> — this case advances
              automatically once the fee is paid (no staff action here).
            </div>
          ) : null}
          {c.resolutionNotes ? (
            <div className="rounded-lg bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-800">
              <span className="font-semibold">Resolution:</span> {c.resolutionNotes}
            </div>
          ) : null}
          {c.issuedDocSerial ? (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-800">
              Document issued — serial <span className="font-mono">{c.issuedDocSerial}</span>
              {c.issuedDocVerifyUrl ? (
                <>
                  {' · '}
                  <a href={c.issuedDocVerifyUrl} target="_blank" rel="noreferrer" className="underline">
                    verify ↗
                  </a>
                </>
              ) : null}
            </div>
          ) : null}
          {c.state === 'REJECTED' && c.rejectionReason ? <ErrorNote message={`Rejected: ${c.rejectionReason}`} /> : null}
          {c.state === 'WITHDRAWN' && c.withdrawnReason ? <ErrorNote message={`Withdrawn: ${c.withdrawnReason}`} /> : null}

          {isStaff ? (
            <div className="space-y-3 border-t border-slate-100 pt-4">
              <div className="flex flex-wrap gap-2">
                {c.state === 'SUBMITTED' ? (
                  <button className="btn-primary" disabled={busy} onClick={() => act(() => api.post(`/api/v1/m42/consular-cases/${c.id}/acknowledge`))}>
                    {busy ? 'Acknowledging…' : 'Acknowledge'}
                  </button>
                ) : null}

                {c.state === 'ACKNOWLEDGED' && hasFee ? (
                  <button className="btn-primary" disabled={busy} onClick={() => act(() => api.post(`/api/v1/m42/consular-cases/${c.id}/fee`))}>
                    {busy ? 'Requesting…' : 'Request fee (SeyPay intent)'}
                  </button>
                ) : null}
                {c.state === 'ACKNOWLEDGED' && c.type === 'WELFARE_ASSIST' ? (
                  <button className="btn-primary" disabled={busy} onClick={() => act(() => api.post(`/api/v1/m42/consular-cases/${c.id}/start`))}>
                    {busy ? 'Starting…' : 'Start (no fee)'}
                  </button>
                ) : null}
                {(c.state === 'ACKNOWLEDGED' || c.state === 'IN_PROGRESS') && c.type === 'PASSPORT_RENEWAL' ? (
                  <button className="btn-primary" disabled={busy} onClick={() => setShowLinkM04((s) => !s)}>
                    {c.m04ApplicationRef ? 'Update M04 application ref' : 'Link M04 application'}
                  </button>
                ) : null}

                {c.type === 'EMERGENCY_TRAVEL_DOC' ? (
                  <button className="btn-secondary" disabled={busy} onClick={() => setShowVerifyPassport((s) => !s)}>
                    Verify claimed passport no.
                  </button>
                ) : null}

                {c.state === 'IN_PROGRESS' ? (
                  <button className="btn-success" disabled={busy} onClick={() => setShowResolve((s) => !s)}>
                    Resolve
                  </button>
                ) : null}

                {['SUBMITTED', 'ACKNOWLEDGED', 'FEE_PENDING', 'IN_PROGRESS'].includes(c.state) ? (
                  <>
                    <button className="btn-danger" disabled={busy} onClick={() => setShowReject((s) => !s)}>
                      Reject
                    </button>
                    <button className="btn-secondary" disabled={busy} onClick={() => setShowWithdraw((s) => !s)}>
                      Withdraw
                    </button>
                  </>
                ) : null}
              </div>

              {showLinkM04 ? (
                <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50/50 p-3">
                  <Field label="M04 PassportApplication reference">
                    <input className="field-input" value={m04Ref} onChange={(e) => setM04Ref(e.target.value)} placeholder="e.g. m04-application-cuid" />
                  </Field>
                  <p className="text-xs text-slate-500">
                    Entered directly by the officer from M04's own console — M42 does not call M04's write API (see the module's build report).
                  </p>
                  <button
                    className="btn-primary"
                    disabled={busy || m04Ref.trim().length === 0}
                    onClick={() => act(() => api.post(`/api/v1/m42/consular-cases/${c.id}/link-passport-application`, { m04ApplicationRef: m04Ref.trim() }))}
                  >
                    {busy ? 'Saving…' : 'Save reference'}
                  </button>
                </div>
              ) : null}

              {showVerifyPassport ? (
                <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50/50 p-3">
                  <Field label="Claimed passport number">
                    <input className="field-input font-mono" value={passportNumber} onChange={(e) => setPassportNumber(e.target.value)} />
                  </Field>
                  <p className="text-xs text-slate-500">Best-effort, non-blocking cross-check against M04's real pass:verify — proceeds either way.</p>
                  <button
                    className="btn-secondary"
                    disabled={busy || passportNumber.trim().length < 4}
                    onClick={() => act(() => api.post(`/api/v1/m42/consular-cases/${c.id}/verify-passport`, { passportNumber: passportNumber.trim() }))}
                  >
                    {busy ? 'Checking…' : 'Run check'}
                  </button>
                </div>
              ) : null}

              {showResolve ? (
                <div className="space-y-2 rounded-lg border border-emerald-200 bg-emerald-50/50 p-3">
                  <Field label={c.type === 'WELFARE_ASSIST' ? 'Resolution notes (required)' : 'Resolution notes (optional)'}>
                    <textarea className="field-input" rows={2} value={resolutionNotes} onChange={(e) => setResolutionNotes(e.target.value)} />
                  </Field>
                  <button
                    className="btn-success"
                    disabled={busy || (c.type === 'WELFARE_ASSIST' && resolutionNotes.trim().length === 0)}
                    onClick={() => act(() => api.post(`/api/v1/m42/consular-cases/${c.id}/resolve`, { resolutionNotes: resolutionNotes.trim() || undefined }))}
                  >
                    {busy ? 'Resolving…' : 'Confirm resolve'}
                  </button>
                </div>
              ) : null}

              {showReject ? (
                <div className="space-y-2 rounded-lg border border-rose-200 bg-rose-50/50 p-3">
                  <Field label="Rejection reason">
                    <textarea className="field-input" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
                  </Field>
                  <button
                    className="btn-danger"
                    disabled={busy || reason.trim().length < 3}
                    onClick={() => act(() => api.post(`/api/v1/m42/consular-cases/${c.id}/reject`, { reason }))}
                  >
                    {busy ? 'Submitting…' : 'Confirm reject'}
                  </button>
                </div>
              ) : null}
              {showWithdraw ? (
                <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50/50 p-3">
                  <Field label="Withdrawal reason (optional)">
                    <textarea className="field-input" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
                  </Field>
                  <button
                    className="btn-secondary"
                    disabled={busy}
                    onClick={() => act(() => api.post(`/api/v1/m42/consular-cases/${c.id}/withdraw`, { reason: reason.trim() || undefined }))}
                  >
                    {busy ? 'Submitting…' : 'Confirm withdraw'}
                  </button>
                </div>
              ) : null}
            </div>
          ) : (
            <RoleNotice roles={['CONSULAR_OFFICER', 'MISSION_HEAD']} />
          )}

          <div className="border-t border-slate-100 pt-3 text-xs text-slate-400">
            Filed {formatDateTime(c.createdAt)} · last updated {formatDateTime(c.updatedAt)}
          </div>
        </div>
      ) : null}
    </Modal>
  );
}

function CreateCaseModal({ missions, onClose, onDone }: { missions: DiplomaticMission[]; onClose: () => void; onDone: () => void }) {
  const [missionCode, setMissionCode] = useState(missions[0]?.code ?? '');
  const [type, setType] = useState<ConsularCaseType>('EMERGENCY_TRAVEL_DOC');
  const [applicantNin, setApplicantNin] = useState('');
  const [applicantName, setApplicantName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [countryOfResidence, setCountryOfResidence] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Type-specific fields
  const [currentPassportNo, setCurrentPassportNo] = useState('');
  const [renewalReason, setRenewalReason] = useState('');
  const [lostOrStolen, setLostOrStolen] = useState<'LOST' | 'STOLEN' | 'DAMAGED'>('LOST');
  const [policeReportRef, setPoliceReportRef] = useState('');
  const [travelDate, setTravelDate] = useState('');
  const [destinationCountry, setDestinationCountry] = useState('Seychelles');
  const [assistanceType, setAssistanceType] = useState('');
  const [description, setDescription] = useState('');
  const [notarialAct, setNotarialAct] = useState('');
  const [documentDescription, setDocumentDescription] = useState('');

  const ninInvalid = applicantNin.trim().length > 0 && !NIN_PATTERN.test(applicantNin.trim());

  const detailsValid = useMemo(() => {
    if (type === 'PASSPORT_RENEWAL') return renewalReason.trim().length >= 3;
    if (type === 'EMERGENCY_TRAVEL_DOC') return travelDate.length > 0 && destinationCountry.trim().length >= 2;
    if (type === 'WELFARE_ASSIST') return assistanceType.trim().length >= 3 && description.trim().length >= 10;
    return notarialAct.trim().length >= 3 && documentDescription.trim().length >= 3;
  }, [type, renewalReason, travelDate, destinationCountry, assistanceType, description, notarialAct, documentDescription]);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      let details: Record<string, unknown>;
      if (type === 'PASSPORT_RENEWAL') {
        details = { currentPassportNo: currentPassportNo.trim() || undefined, renewalReason };
      } else if (type === 'EMERGENCY_TRAVEL_DOC') {
        details = { lostOrStolen, policeReportRef: policeReportRef.trim() || undefined, travelDate, destinationCountry };
      } else if (type === 'WELFARE_ASSIST') {
        details = { assistanceType, description };
      } else {
        details = { notarialAct, documentDescription };
      }
      await api.post('/api/v1/m42/consular-cases', {
        missionCode,
        type,
        applicantNin: applicantNin.trim(),
        applicantName,
        contactEmail,
        contactPhone: contactPhone.trim() || undefined,
        countryOfResidence,
        details,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to file case');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="File a consular case" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <p className="text-xs text-slate-500">
          Filed by consular staff on the applicant's behalf — the applicant's own NIN identifies them (real SeyID remote T2 eKYC path).
        </p>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Mission">
            <select className="field-input" value={missionCode} onChange={(e) => setMissionCode(e.target.value)}>
              {missions.map((m) => (
                <option key={m.id} value={m.code}>
                  {m.name} ({m.code})
                </option>
              ))}
            </select>
          </Field>
          <Field label="Case type">
            <select className="field-input" value={type} onChange={(e) => setType(e.target.value as ConsularCaseType)}>
              {CASE_TYPES.map((t) => (
                <option key={t} value={t}>
                  {CASE_TYPE_LABEL[t]}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Applicant NIN">
            <input className="field-input font-mono" value={applicantNin} onChange={(e) => setApplicantNin(e.target.value)} placeholder="NNN-NNNN-N-N-NN" />
            {ninInvalid ? <p className="mt-1 text-xs text-rose-600">Expected format NNN-NNNN-N-N-NN.</p> : null}
          </Field>
          <Field label="Applicant full name">
            <input className="field-input" value={applicantName} onChange={(e) => setApplicantName(e.target.value)} />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Contact email">
            <input type="email" className="field-input" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} />
          </Field>
          <Field label="Contact phone (optional)">
            <input className="field-input" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} />
          </Field>
        </div>
        <Field label="Country of residence">
          <input className="field-input" value={countryOfResidence} onChange={(e) => setCountryOfResidence(e.target.value)} />
        </Field>

        <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-3">
          <div className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">{CASE_TYPE_LABEL[type]} details</div>
          {type === 'PASSPORT_RENEWAL' ? (
            <div className="space-y-3">
              <Field label="Current passport no. (optional)">
                <input className="field-input font-mono" value={currentPassportNo} onChange={(e) => setCurrentPassportNo(e.target.value)} />
              </Field>
              <Field label="Renewal reason">
                <input className="field-input" value={renewalReason} onChange={(e) => setRenewalReason(e.target.value)} placeholder="e.g. Expiring within 6 months" />
              </Field>
            </div>
          ) : type === 'EMERGENCY_TRAVEL_DOC' ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Lost or stolen">
                  <select className="field-input" value={lostOrStolen} onChange={(e) => setLostOrStolen(e.target.value as typeof lostOrStolen)}>
                    <option value="LOST">Lost</option>
                    <option value="STOLEN">Stolen</option>
                    <option value="DAMAGED">Damaged</option>
                  </select>
                </Field>
                <Field label="Police report ref (optional)">
                  <input className="field-input" value={policeReportRef} onChange={(e) => setPoliceReportRef(e.target.value)} />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Travel date">
                  <input type="date" className="field-input" value={travelDate} onChange={(e) => setTravelDate(e.target.value)} />
                </Field>
                <Field label="Destination country">
                  <input className="field-input" value={destinationCountry} onChange={(e) => setDestinationCountry(e.target.value)} />
                </Field>
              </div>
            </div>
          ) : type === 'WELFARE_ASSIST' ? (
            <div className="space-y-3">
              <Field label="Assistance type">
                <input className="field-input" value={assistanceType} onChange={(e) => setAssistanceType(e.target.value)} placeholder="e.g. Hospitalisation" />
              </Field>
              <Field label="Description">
                <textarea className="field-input" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
              </Field>
            </div>
          ) : (
            <div className="space-y-3">
              <Field label="Notarial act">
                <input className="field-input" value={notarialAct} onChange={(e) => setNotarialAct(e.target.value)} placeholder="e.g. Power of attorney" />
              </Field>
              <Field label="Document description">
                <textarea className="field-input" rows={2} value={documentDescription} onChange={(e) => setDocumentDescription(e.target.value)} />
              </Field>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={submit}
            disabled={
              busy ||
              !missionCode ||
              ninInvalid ||
              applicantNin.trim().length === 0 ||
              applicantName.trim().length < 2 ||
              !contactEmail.includes('@') ||
              countryOfResidence.trim().length < 2 ||
              !detailsValid
            }
          >
            {busy ? 'Filing…' : 'File case'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
